<template>
  <div class="report-analytic-item-container">
    <div class="report-analytic-item">
      <div class="report-analytic-data-container" @click="redirect" @mouseover="showRedirectButton = true" @mouseleave="showRedirectButton = false">
        <div class="report-analytic-data">
          <mf-icon color="marca-0-l40" size="20" :fill="isRecurrent">{{ getModelIcon }}</mf-icon>
          <div class="data-labels">
            <span class="data-title">{{ title }}</span>
            <span class="data-model-and-period">{{ getModelLabel }} {{ isRecurrent ? ' - ' : ' | ' }} {{ getModelPeriod }}</span>
          </div>
        </div>
        <mf-icon v-if="showRedirectButton" color="marca-0-l40" size="20">open_in_new</mf-icon>
      </div>
      <div class="divider-line"></div>
      <div class="report-analytic-filters">
        <report-analytic-filter v-for="(filter, index) in filters" :key="index" :items="filter" :type="index" />
      </div>
    </div>
    <div class="report-analytic-actions">
      <div v-if="isProcessing" class="processing-action">
        <div class="processing-spinner"></div>
        <span>Processando análise</span>
      </div>
      <div v-if="status === 'FAILED'" class="status-failed-action">
        <mf-tooltip top max-width="220">
          <template #activator="{ on }">
            <mf-info-card type="error" icon="warning" description="Falha no processamento" variant="error" />
          </template>
          <span>Ocorreu um erro durante o processamento na última ação desta análise.</span>
        </mf-tooltip>
      </div>
      <div v-if="isRecurrent" class="edit-action">
        <mf-tooltip top :disabled="!isDefault">
          <template #activator="{ on }">
            <mf-button text label="Editar" icon="edit" color="marca-0-l30" :disabled="isDefault || isProcessing" @click="onEdit" />
          </template>
          <span>Não é possível editar ou excluir a análise geral.</span>
        </mf-tooltip>
      </div>
      <div class="delete-action">
        <mf-tooltip top :disabled="!isDefault">
          <template #activator="{ on }">
            <mf-button text label="Excluir" icon="delete" color="vermelho-0" :disabled="isDefault || isProcessing" @click="onDelete" />
          </template>
          <span>Não é possível editar ou excluir a análise geral.</span>
        </mf-tooltip>
      </div>
    </div>
    <delete-report-dialog v-if="deleteDialog" v-model="deleteDialog" :report-id="id" :report-model="model" :report-title="title" />
    <new-report-dialog v-if="newReportDialog" v-model="newReportDialog" :report="getEditableReport" @analysis-generated="refetchData" />
    <div v-if="isProcessing" class="processing-blur"></div>
  </div>
</template>

<script>
import ReportAnalyticFilter from './ReportAnalyticFilter.vue'
import DeleteReportDialog from './DeleteReportDialog.vue'
import NewReportDialog from '../NewReportDialog/NewReportDialog.vue'
import { getDaysIntervalLabel } from '@/helpers/datesLabels.js'
import { DateTime } from 'luxon'
import { mapActions } from 'vuex'

export default {
  name: 'ReportAnalyticItem',
  components: { ReportAnalyticFilter, DeleteReportDialog, NewReportDialog },
  props: {
    id: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'rfv',
      validator: (value) => ['rfv', 'dre'].includes(value),
    },
    model: {
      type: String,
      default: '',
    },
    status: {
      type: String,
      default: '',
    },
    period: {
      type: Object,
      default: () => {},
    },
    filters: {
      type: Object,
      default: () => {},
    },
    isProcessing: {
      type: Boolean,
      default: false,
    },
    isDefault: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    showRedirectButton: false,
    deleteDialog: false,
    newReportDialog: false,
    modelTypes: {
      MONTHLY: 'Mensal',
      WEEKLY: 'Semanal',
    },
  }),
  computed: {
    isRecurrent() {
      return this.model === 'RECURRENT'
    },
    getEditableReport() {
      return {
        id: this.id,
        filters: {
          name: this.title,
          campaigns: this.filters.offersScheduleIds || [],
          units: this.filters.units ? this.filters.units.map((unit) => unit.id_erp) : [],
          products: this.filters.products || [],
          personType: this.filters.clientsType || [],
        },
        weeklyRecurrence: this.period?.initialWeekDay,
        recurrent: this.isRecurrent,
        reportType: this.type.toUpperCase(),
        recurrence: this.period?.modelType,
        analyzedPeriodInitialDate: this.period?.analyzedPeriod?.initialDate,
        analyzedPeriodFinalDate: this.period?.analyzedPeriod?.finalDate,
        referencePeriodInitialDate: this.period?.referencePeriod?.initialDate,
        referencePeriodFinalDate: this.period?.referencePeriod?.finalDate,
      }
    },
    getModelLabel() {
      return this.isRecurrent ? 'Análise recorrente' : 'Análise isolada'
    },
    getModelIcon() {
      return this.isRecurrent ? 'source_notes' : 'article'
    },
    getModelPeriod() {
      if (this.isRecurrent) {
        let modelType = this.modelTypes[this.period.modelType]
        if (this.period.modelType === 'WEEKLY') {
          modelType = `${modelType} | ${getDaysIntervalLabel(this.period.initialWeekDay)}`
        }
        return modelType
      } else {
        const initialDate = DateTime.fromISO(this.period.analyzedPeriod.initialDate).toFormat('dd/MM/yyyy')
        const finalDate = DateTime.fromISO(this.period.analyzedPeriod.finalDate).toFormat('dd/MM/yyyy')
        return `${initialDate} - ${finalDate}`
      }
    },
    getPeriodLabel() {
      return ''
    },
  },
  methods: {
    ...mapActions(['getHubAnalyticsConfigurations', 'getHubAnalyticsLimits']),
    onDelete() {
      this.deleteDialog = true
    },
    onEdit() {
      this.newReportDialog = true
    },
    async refetchData() {
      await this.getHubAnalyticsLimits()
      await this.getHubAnalyticsConfigurations({ type: 'RFV' })
      await this.getHubAnalyticsConfigurations({ type: 'DRE' })
    },
    redirect() {
      const name = this.type === 'rfv' ? 'Classificação RFV' : 'Faturamento incremental'
      const routeData = this.$router.resolve({ name, params: { analysisId: this.id } })

      window.open(routeData.href, '_blank')
    },
  },
}
</script>

<style lang="scss" scoped>
.report-analytic-item-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid $cinza-l80;
  padding: 8px 16px;
  .report-analytic-item {
    display: flex;
    width: 60%;
    align-items: center;
    .report-analytic-data-container {
      width: 44%;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;
      padding: 2px;
      .report-analytic-data {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
        .data-labels {
          display: flex;
          flex-direction: column;
          gap: 2px;
          .data-title {
            color: $marca-0-l40;
            font-size: 14px;
            font-weight: 700;
            line-height: 120%;
            letter-spacing: 0.4px;
          }
          .data-model-and-period {
            color: $cinza-d10;
            font-size: 12px;
            font-weight: 400;
            line-height: 120%;
            letter-spacing: 0.12px;
          }
        }
      }
    }
  }
  .divider-line {
    width: 22px;
    height: 2px;
    transform: rotate(90deg);
    border-radius: 15%;
    background-color: $marca-0-l20;
  }
  .report-analytic-filters {
    display: flex;
    flex-direction: row;
    gap: 15px;
  }

  .report-analytic-actions {
    display: flex;
    flex-direction: row;
    .processing-action {
      display: flex;
      flex-direction: row;
      gap: 10px;
      align-items: center;
      margin-right: 20px;
      z-index: 1;
      .processing-spinner {
        width: 16px;
        height: 16px;
        border: 3.5px solid transparent;
        border-top-color: $marca-0-l40;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      }
      span {
        color: $marca-0-l40;
        font-size: 14px;
        font-weight: 700;
        line-height: 20px;
      }
    }
  }
  .processing-blur {
    position: absolute;
    min-height: 50px;
    width: 100%;
    background-color: white;
    opacity: 0.5;
  }
}

.report-analytic-data-container:hover {
  background-color: #d2e9ff4d;
  border-radius: 8px;
  background-clip: padding-box;
}
</style>
